import { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, doc, updateDoc, orderBy, query } from "firebase/firestore";
import AdminNav from "../pages/Adminnav";

// Try to import chart.js
let ChartJS, Line, Bar, Doughnut;
let CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler;
let chartsAvailable = false;

try {
  const chartJs = require('chart.js');
  const reactChartJs2 = require('react-chartjs-2');
  
  ChartJS = chartJs.Chart;
  CategoryScale = chartJs.CategoryScale;
  LinearScale = chartJs.LinearScale;
  PointElement = chartJs.PointElement;
  LineElement = chartJs.LineElement;
  BarElement = chartJs.BarElement;
  ArcElement = chartJs.ArcElement;
  Title = chartJs.Title;
  Tooltip = chartJs.Tooltip;
  Legend = chartJs.Legend;
  Filler = chartJs.Filler;
  
  Line = reactChartJs2.Line;
  Bar = reactChartJs2.Bar;
  Doughnut = reactChartJs2.Doughnut;
  
  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
  );
  
  chartsAvailable = true;
} catch (e) {
  chartsAvailable = false;
}

const STATUS_STYLES = {
  Pending: { bg: "#FEF3C7", text: "#D97706", dot: "#D97706", label: "Pending" },
  Delivered: { bg: "#D1FAE5", text: "#059669", dot: "#059669", label: "Delivered" },
  Returned: { bg: "#FEE2E2", text: "#DC2626", dot: "#DC2626", label: "Returned" },
  Completed: { bg: "#D1FAE5", text: "#059669", dot: "#059669", label: "Completed" },
  Processing: { bg: "#DBEAFE", text: "#2563EB", dot: "#2563EB", label: "Processing" },
  Cancelled: { bg: "#FEE2E2", text: "#DC2626", dot: "#DC2626", label: "Cancelled" },
};

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Date filter state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Financial details state
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [costOfSale, setCostOfSale] = useState("");
  const [shippingCost, setShippingCost] = useState("");
  const [otherExpense, setOtherExpense] = useState("");
  const [savingFinancial, setSavingFinancial] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setOrders(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (error) => {
        console.error("Orders fetch error:", error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const updateStatus = async (id, status) => {
    await updateDoc(doc(db, "orders", id), { status });
  };

  const handleOrderSelect = (orderId) => {
    setSelectedOrderId(orderId);
    const order = orders.find(o => o.id === orderId);
    if (order) {
      setCostOfSale(order.costOfSale !== undefined && order.costOfSale !== null ? order.costOfSale : "");
      setShippingCost(order.shippingCost !== undefined && order.shippingCost !== null ? order.shippingCost : "");
      setOtherExpense(order.otherExpense !== undefined && order.otherExpense !== null ? order.otherExpense : "");
      setIsEditing(false);
    }
  };

  const saveFinancialDetails = async () => {
    if (!selectedOrderId) {
      alert("⚠️ Please select an order first.");
      return;
    }

    const order = orders.find(o => o.id === selectedOrderId);
    if (!order) return;

    const costOfSaleNum = costOfSale === "" ? 0 : Number(costOfSale);
    const shippingCostNum = shippingCost === "" ? 0 : Number(shippingCost);
    const otherExpenseNum = otherExpense === "" ? 0 : Number(otherExpense);
    
    const totalExpense = costOfSaleNum + shippingCostNum + otherExpenseNum;
    const profit = Number(order.total) - totalExpense;
    const isLoss = profit < 0;

    setSavingFinancial(true);

    try {
      await updateDoc(doc(db, "orders", selectedOrderId), {
        costOfSale: costOfSaleNum,
        shippingCost: shippingCostNum,
        otherExpense: otherExpenseNum,
        totalExpense: totalExpense,
        profit: profit,
        isLoss: isLoss
      });
      
      setCostOfSale("");
      setShippingCost("");
      setOtherExpense("");
      setSelectedOrderId("");
      setIsEditing(false);
      
      alert("✅ Financial details saved successfully!");
    } catch (error) {
      console.error("Error saving financial details:", error);
      alert("❌ Error saving financial details. Please try again.");
    }

    setSavingFinancial(false);
  };

  const deleteFinancialDetails = async () => {
    if (!selectedOrderId) {
      alert("⚠️ Please select an order first.");
      return;
    }

    const confirmDelete = window.confirm("⚠️ Are you sure you want to delete all financial data for this order?");
    if (!confirmDelete) return;

    setSavingFinancial(true);

    try {
      await updateDoc(doc(db, "orders", selectedOrderId), {
        costOfSale: null,
        shippingCost: null,
        otherExpense: null,
        totalExpense: null,
        profit: null,
        isLoss: null
      });
      
      setCostOfSale("");
      setShippingCost("");
      setOtherExpense("");
      setSelectedOrderId("");
      setIsEditing(false);
      
      alert("🗑️ Financial data deleted successfully!");
    } catch (error) {
      console.error("Error deleting financial details:", error);
      alert("❌ Error deleting financial details. Please try again.");
    }

    setSavingFinancial(false);
  };

  const editFinancialDetails = () => {
    setIsEditing(true);
    const order = orders.find(o => o.id === selectedOrderId);
    if (order) {
      setCostOfSale(order.costOfSale !== undefined && order.costOfSale !== null ? order.costOfSale : "");
      setShippingCost(order.shippingCost !== undefined && order.shippingCost !== null ? order.shippingCost : "");
      setOtherExpense(order.otherExpense !== undefined && order.otherExpense !== null ? order.otherExpense : "");
    }
  };

  const resetFinancialForm = () => {
    setSelectedOrderId("");
    setCostOfSale("");
    setShippingCost("");
    setOtherExpense("");
    setIsEditing(false);
  };

  // Filter orders by date range
  const filterOrdersByDate = (ordersList) => {
    if (!startDate && !endDate) return ordersList;
    
    return ordersList.filter(o => {
      if (!o.createdAt) return true;
      const date = o.createdAt.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
      const orderDate = new Date(date);
      orderDate.setHours(0, 0, 0, 0);
      
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        return orderDate >= start && orderDate <= end;
      } else if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        return orderDate >= start;
      } else if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return orderDate <= end;
      }
      return true;
    });
  };

  const filteredOrders = filterOrdersByDate(orders.filter((o) => {
    const matchesFilter = filter === "All" || (o.status || "Pending") === filter;
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      o.customerName?.toLowerCase().includes(term) ||
      o.phone?.toLowerCase().includes(term) ||
      o.city?.toLowerCase().includes(term);
    return matchesFilter && matchesSearch;
  }));

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const counts = {
    All: orders.length,
    Pending: orders.filter((o) => (o.status || "Pending") === "Pending").length,
    Delivered: orders.filter((o) => o.status === "Delivered").length,
    Returned: orders.filter((o) => o.status === "Returned").length,
    Completed: orders.filter((o) => o.status === "Completed").length,
    Processing: orders.filter((o) => o.status === "Processing").length,
    Cancelled: orders.filter((o) => o.status === "Cancelled").length,
  };

  const summary = {
    totalOrders: filteredOrders.length,
    totalRevenue: filteredOrders.reduce((sum, o) => sum + (o.total || 0), 0),
    totalExpenses: filteredOrders.reduce((sum, o) => sum + (o.totalExpense || 0), 0),
    totalProfit: filteredOrders.reduce((sum, o) => sum + ((o.profit || 0) > 0 ? o.profit : 0), 0),
    totalLoss: filteredOrders.reduce((sum, o) => sum + ((o.profit || 0) < 0 ? Math.abs(o.profit) : 0), 0),
  };

  const getMonthlySummary = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const monthlyOrders = orders.filter(o => {
      if (!o.createdAt) return false;
      const date = o.createdAt.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });

    const revenue = monthlyOrders.reduce((sum, o) => sum + (o.total || 0), 0);
    const expenses = monthlyOrders.reduce((sum, o) => sum + (o.totalExpense || 0), 0);
    const profit = revenue - expenses;
    const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;

    return { revenue, expenses, profit, profitMargin };
  };

  const monthlySummary = getMonthlySummary();

  // Chart data
  const getMonthlyRevenueData = () => {
    const months = [];
    const revenueData = [];
    const expenseData = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = month.toLocaleString('default', { month: 'short' });
      months.push(monthName);
      
      const monthOrders = orders.filter(o => {
        if (!o.createdAt) return false;
        const date = o.createdAt.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
        return date.getMonth() === month.getMonth() && date.getFullYear() === month.getFullYear();
      });
      
      const revenue = monthOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      const expenses = monthOrders.reduce((sum, o) => sum + (o.totalExpense || 0), 0);
      revenueData.push(revenue);
      expenseData.push(expenses);
    }
    
    return { months, revenueData, expenseData };
  };

  const monthlyData = getMonthlyRevenueData();

  const revenueLineData = chartsAvailable ? {
    labels: monthlyData.months,
    datasets: [
      {
        label: 'Revenue',
        data: monthlyData.revenueData,
        borderColor: '#8B5CF6',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#8B5CF6',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
      },
      {
        label: 'Expenses',
        data: monthlyData.expenseData,
        borderColor: '#F43F5E',
        backgroundColor: 'rgba(244, 63, 94, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#F43F5E',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
      }
    ]
  } : null;

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: { family: 'Inter', size: 12, weight: '500' }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.dataset.label + ': Rs. ' + context.parsed.y.toFixed(2);
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) { return 'Rs. ' + value; },
          font: { family: 'Inter', size: 11 }
        },
        grid: { color: 'rgba(0,0,0,0.05)' }
      },
      x: {
        grid: { display: false },
        ticks: { font: { family: 'Inter', size: 11 } }
      }
    }
  };

  const statusData = chartsAvailable ? {
    labels: ['Completed', 'Pending', 'Processing', 'Cancelled'],
    datasets: [{
      data: [counts.Completed || 0, counts.Pending || 0, counts.Processing || 0, counts.Cancelled || 0],
      backgroundColor: ['#22D3EE', '#FBBF24', '#8B5CF6', '#F43F5E'],
      borderWidth: 2,
      borderColor: '#fff',
      hoverOffset: 8
    }]
  } : null;

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          font: { family: 'Inter', size: 12, weight: '500' }
        }
      }
    },
    cutout: '60%'
  };

  const formatDate = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  const formatCurrency = (amount) => {
    return `Rs. ${amount?.toFixed(2) || '0.00'}`;
  };

  const selectedOrder = orders.find(o => o.id === selectedOrderId);
  const hasExistingFinancialData = selectedOrder && 
    (selectedOrder.costOfSale !== undefined && selectedOrder.costOfSale !== null && selectedOrder.costOfSale !== 0);

  const getStatusCount = (status) => {
    if (status === "All") return filteredOrders.length;
    return filteredOrders.filter(o => (o.status || "Pending") === status).length;
  };

  const getStatusColor = (status) => {
    const colors = {
      Pending: { bg: "#FEF3C7", text: "#D97706" },
      Delivered: { bg: "#D1FAE5", text: "#059669" },
      Completed: { bg: "#D1FAE5", text: "#059669" },
      Returned: { bg: "#FEE2E2", text: "#DC2626" },
      Processing: { bg: "#DBEAFE", text: "#2563EB" },
      Cancelled: { bg: "#FEE2E2", text: "#DC2626" },
    };
    return colors[status] || colors.Pending;
  };

  const ChartSection = () => {
    if (!chartsAvailable) {
      return null;
    }

    return (
      <div className="charts-grid" style={{ marginBottom: '24px' }}>
        <div className="card glass-card">
          <div className="card-header">
            <div>
              <div className="card-title" style={{ color: '#8B5CF6' }}>📈 Revenue Overview</div>
              <div className="card-subtitle">Last 6 months revenue & expenses</div>
            </div>
          </div>
          <div className="chart-container" style={{ height: '280px', position: 'relative' }}>
            <Line data={revenueLineData} options={lineOptions} />
          </div>
        </div>

        <div className="card glass-card">
          <div className="card-header">
            <div>
              <div className="card-title" style={{ color: '#8B5CF6' }}>📊 Order Status</div>
              <div className="card-subtitle">Distribution by status</div>
            </div>
          </div>
          <div className="chart-container-sm" style={{ height: '220px', position: 'relative' }}>
            <Doughnut data={statusData} options={doughnutOptions} />
          </div>
        </div>
      </div>
    );
  };

  // Clear date filter
  const clearDateFilter = () => {
    setStartDate("");
    setEndDate("");
    setShowDatePicker(false);
  };

  // Get date range display text
  const getDateRangeText = () => {
    if (startDate && endDate) {
      return `${new Date(startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} - ${new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else if (startDate) {
      return `From ${new Date(startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else if (endDate) {
      return `Until ${new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return "Select Date Range";
  };

  return (
    <div className="min-h-screen w-full" style={{ background: "linear-gradient(135deg, #F0F4FF 0%, #FAFBFF 100%)" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        
        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease forwards;
          opacity: 0;
        }
        
        .animate-slideIn {
          animation: slideIn 0.4s ease forwards;
          opacity: 0;
        }
        
        .glass-card {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
        }
        
        .dashboard-wrapper {
          display: flex;
          min-height: 100vh;
        }
        
        /* Sidebar Modern */
        .sidebar {
          width: 260px;
          background: linear-gradient(180deg, #0F172A 0%, #1E293B 100%);
          color: #94A3B8;
          position: fixed;
          height: 100vh;
          overflow-y: auto;
          transition: all 0.3s ease;
          z-index: 1000;
          padding: 24px 0;
          border-right: 1px solid rgba(255,255,255,0.05);
        }
        
        .sidebar-brand {
          padding: 0 24px 28px 24px;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          margin-bottom: 20px;
          position: relative;
        }
        
        .sidebar-brand::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 20%;
          right: 20%;
          height: 2px;
          background: linear-gradient(90deg, transparent, #8B5CF6, transparent);
        }
        
        .sidebar-brand h1 {
          color: #FFFFFF;
          font-size: 22px;
          font-weight: 800;
          letter-spacing: -0.5px;
        }
        
        .sidebar-brand h1 span {
          background: linear-gradient(135deg, #8B5CF6, #A78BFA);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .sidebar-brand p {
          color: #64748B;
          font-size: 12px;
          margin-top: 4px;
        }
        
        .sidebar-menu {
          padding: 0 12px;
        }
        
        .sidebar-menu .menu-label {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #475569;
          padding: 16px 12px 8px 12px;
          font-weight: 600;
        }
        
        .sidebar-menu .menu-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 16px;
          border-radius: 8px;
          color: #94A3B8;
          transition: all 0.3s ease;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 2px;
          text-decoration: none;
          position: relative;
        }
        
        .sidebar-menu .menu-item::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 0;
          background: linear-gradient(180deg, #8B5CF6, #A78BFA);
          border-radius: 0 4px 4px 0;
          transition: height 0.3s ease;
        }
        
        .sidebar-menu .menu-item:hover::before,
        .sidebar-menu .menu-item.active::before {
          height: 60%;
        }
        
        .sidebar-menu .menu-item:hover {
          background: rgba(255,255,255,0.05);
          color: #FFFFFF;
        }
        
        .sidebar-menu .menu-item.active {
          background: rgba(139, 92, 246, 0.1);
          color: #A78BFA;
        }
        
        .sidebar-menu .menu-item .icon {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }
        
        .sidebar-menu .menu-item .badge {
          margin-left: auto;
          background: rgba(139, 92, 246, 0.2);
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 11px;
          color: #A78BFA;
        }
        
        .sidebar-menu .menu-item.active .badge {
          background: rgba(139, 92, 246, 0.3);
          color: #A78BFA;
        }
        
        .main-content {
          margin-left: 260px;
          padding: 24px 32px;
          min-height: 100vh;
          flex: 1;
        }
        
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 28px;
          flex-wrap: wrap;
          gap: 12px;
          animation: fadeInUp 0.6s ease;
        }
        
        .page-header h1 {
          font-size: 28px;
          font-weight: 800;
          background: linear-gradient(135deg, #0F172A, #3B82F6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .page-header p {
          color: #64748B;
          font-size: 14px;
          margin-top: 2px;
        }
        
        .page-header .header-actions {
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
        }
        
        /* Date Picker Styles */
        .date-picker-wrapper {
          position: relative;
          display: inline-block;
        }
        
        .date-picker-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: white;
          border: 2px solid #E2E8F0;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 500;
          color: #64748B;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
        }
        
        .date-picker-toggle:hover {
          border-color: #8B5CF6;
          box-shadow: 0 4px 12px rgba(139, 92, 246, 0.1);
        }
        
        .date-picker-toggle.active {
          border-color: #8B5CF6;
          background: rgba(139, 92, 246, 0.05);
        }
        
        .date-picker-toggle .clear-date {
          margin-left: 4px;
          color: #DC2626;
          cursor: pointer;
          font-size: 14px;
        }
        
        .date-picker-toggle .clear-date:hover {
          transform: scale(1.2);
        }
        
        .date-picker-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          border: 1px solid #E2E8F0;
          z-index: 100;
          min-width: 320px;
          animation: fadeIn 0.2s ease;
        }
        
        .date-picker-dropdown .date-range {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        
        .date-picker-dropdown .date-group label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          color: #64748B;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .date-picker-dropdown .date-group input {
          width: 100%;
          padding: 8px 12px;
          border: 2px solid #E2E8F0;
          border-radius: 8px;
          font-size: 13px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s ease;
        }
        
        .date-picker-dropdown .date-group input:focus {
          outline: none;
          border-color: #8B5CF6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }
        
        .date-picker-dropdown .date-actions {
          display: flex;
          gap: 8px;
          margin-top: 16px;
          justify-content: flex-end;
        }
        
        .date-picker-dropdown .date-actions button {
          padding: 8px 20px;
          border: none;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
        }
        
        .date-picker-dropdown .date-actions .btn-apply {
          background: linear-gradient(135deg, #8B5CF6, #A78BFA);
          color: white;
        }
        
        .date-picker-dropdown .date-actions .btn-apply:hover {
          transform: scale(1.02);
          box-shadow: 0 4px 14px rgba(139, 92, 246, 0.3);
        }
        
        .date-picker-dropdown .date-actions .btn-cancel {
          background: #F1F5F9;
          color: #64748B;
        }
        
        .date-picker-dropdown .date-actions .btn-cancel:hover {
          background: #E2E8F0;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }
        
        .stat-card {
          background: white;
          border-radius: 16px;
          padding: 20px 24px;
          border: 1px solid rgba(0,0,0,0.04);
          transition: all 0.3s ease;
          animation: fadeInUp 0.6s ease;
          position: relative;
          overflow: hidden;
        }
        
        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #8B5CF6, #A78BFA);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .stat-card:hover::before {
          opacity: 1;
        }
        
        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(139, 92, 246, 0.12);
        }
        
        .stat-card .stat-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          margin-bottom: 12px;
        }
        
        .stat-card .stat-label {
          font-size: 12px;
          font-weight: 500;
          color: #64748B;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .stat-card .stat-value {
          font-size: 26px;
          font-weight: 800;
          color: #0F172A;
          margin-top: 4px;
        }
        
        .stat-card .stat-change {
          font-size: 11px;
          margin-top: 4px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 10px;
          border-radius: 12px;
          font-weight: 500;
        }
        
        .stat-card .stat-change.up {
          background: #D1FAE5;
          color: #059669;
        }
        
        .stat-card .stat-change.down {
          background: #FEE2E2;
          color: #DC2626;
        }
        
        .icon-gradient-1 { background: linear-gradient(135deg, #DBEAFE, #EFF6FF); color: #2563EB; }
        .icon-gradient-2 { background: linear-gradient(135deg, #D1FAE5, #ECFDF5); color: #059669; }
        .icon-gradient-3 { background: linear-gradient(135deg, #EDE9FE, #F5F3FF); color: #7C3AED; }
        .icon-gradient-4 { background: linear-gradient(135deg, #FCE7F3, #FDF2F8); color: #DB2777; }
        .icon-gradient-5 { background: linear-gradient(135deg, #FEF3C7, #FFFBEB); color: #D97706; }
        .icon-gradient-6 { background: linear-gradient(135deg, #FEE2E2, #FEF2F2); color: #DC2626; }
        .icon-gradient-7 { background: linear-gradient(135deg, #CCFBF1, #F0FDFA); color: #0D9488; }
        .icon-gradient-8 { background: linear-gradient(135deg, #E0E7FF, #EEF2FF); color: #4F46E5; }
        
        .charts-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }
        
        .card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          border: 1px solid rgba(0,0,0,0.04);
          transition: all 0.3s ease;
        }
        
        .card:hover {
          box-shadow: 0 8px 30px rgba(0,0,0,0.06);
        }
        
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 8px;
        }
        
        .card-title {
          font-size: 16px;
          font-weight: 700;
          color: #0F172A;
        }
        
        .card-subtitle {
          font-size: 13px;
          color: #64748B;
        }
        
        .glass-card {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .table-container {
          overflow-x: auto;
        }
        
        .table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .table th {
          text-align: left;
          padding: 12px 14px;
          font-size: 11px;
          font-weight: 600;
          color: #64748B;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #E8EDF2;
        }
        
        .table td {
          padding: 12px 14px;
          border-bottom: 1px solid #F1F5F9;
          font-size: 13px;
          color: #0F172A;
        }
        
        .table tr {
          transition: all 0.2s ease;
        }
        
        .table tr:hover td {
          background: #F8FAFC;
        }
        
        .table .status-badge {
          padding: 4px 14px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        
        .table .amount {
          font-weight: 600;
        }
        
        .table .order-id {
          font-weight: 600;
          color: #0F172A;
          font-size: 12px;
        }
        
        .filter-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        
        .filter-tab {
          padding: 8px 20px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 500;
          border: 1px solid #E2E8F0;
          background: white;
          color: #64748B;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
        }
        
        .filter-tab:hover {
          background: #F8FAFC;
          border-color: #8B5CF6;
          color: #8B5CF6;
        }
        
        .filter-tab.active {
          background: linear-gradient(135deg, #8B5CF6, #A78BFA);
          color: white;
          border-color: #8B5CF6;
          box-shadow: 0 4px 14px rgba(139, 92, 246, 0.3);
        }
        
        .filter-tab .tab-count {
          background: rgba(0,0,0,0.06);
          padding: 1px 8px;
          border-radius: 10px;
          font-size: 10px;
          margin-left: 4px;
        }
        
        .filter-tab.active .tab-count {
          background: rgba(255,255,255,0.2);
        }
        
        .search-input {
          padding: 8px 16px 8px 36px;
          border: 2px solid #E2E8F0;
          border-radius: 10px;
          font-size: 13px;
          width: 200px;
          background: white;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
        }
        
        .search-input:focus {
          outline: none;
          border-color: #8B5CF6;
          box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.1);
        }
        
        .search-wrapper {
          position: relative;
        }
        
        .search-wrapper .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94A3B8;
          font-size: 14px;
        }
        
        .pagination {
          display: flex;
          gap: 6px;
          justify-content: center;
          margin-top: 20px;
        }
        
        .pagination button {
          padding: 6px 14px;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          background: white;
          color: #475569;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'Inter', sans-serif;
        }
        
        .pagination button:hover {
          background: #F8FAFC;
          border-color: #8B5CF6;
          color: #8B5CF6;
        }
        
        .pagination button.active {
          background: linear-gradient(135deg, #8B5CF6, #A78BFA);
          color: white;
          border-color: #8B5CF6;
        }
        
        .pagination button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        
        .sidebar::-webkit-scrollbar {
          width: 4px;
        }
        .sidebar::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar::-webkit-scrollbar-thumb {
          background: #334155;
          border-radius: 4px;
        }
        
        .chart-container {
          height: 280px;
          position: relative;
        }
        
        .chart-container-sm {
          height: 220px;
          position: relative;
        }
        
        /* ===== FINANCE SECTION MODERN ===== */
        .finance-section {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          border-radius: 20px;
          padding: 32px;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
          position: relative;
          overflow: hidden;
          animation: fadeInUp 0.6s ease;
        }
        
        .finance-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #8B5CF6, #A78BFA, #C4B5FD);
          background-size: 200% 100%;
          animation: shimmer 3s ease-in-out infinite;
        }
        
        .finance-section .finance-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 12px;
        }
        
        .finance-section .finance-header .title-group {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        
        .finance-section .finance-header .title-group .icon-box {
          width: 50px;
          height: 50px;
          border-radius: 14px;
          background: linear-gradient(135deg, #EDE9FE, #F5F3FF);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          animation: float 3s ease-in-out infinite;
        }
        
        .finance-section .finance-header .title-group h3 {
          font-size: 18px;
          font-weight: 700;
          background: linear-gradient(135deg, #0F172A, #8B5CF6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin: 0;
        }
        
        .finance-section .finance-header .title-group p {
          font-size: 13px;
          color: #64748B;
          margin: 0;
        }
        
        .finance-section .finance-header .status-pills {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        
        .finance-section .finance-header .status-pills .pill {
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
          animation: pulse 2s ease-in-out infinite;
        }
        
        .finance-section .finance-header .status-pills .pill.manual {
          background: #F1F5F9;
          color: #64748B;
        }
        
        .finance-section .finance-header .status-pills .pill.added {
          background: #D1FAE5;
          color: #059669;
        }
        
        .finance-section .finance-body {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 24px;
        }
        
        .finance-section .order-selector {
          background: linear-gradient(135deg, #F8FAFC, #F1F5F9);
          border-radius: 14px;
          padding: 20px 24px;
          border: 1px solid #E2E8F0;
        }
        
        .finance-section .order-selector .label {
          font-size: 11px;
          font-weight: 600;
          color: #64748B;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: block;
          margin-bottom: 8px;
        }
        
        .finance-section .order-selector select {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #E2E8F0;
          border-radius: 10px;
          font-size: 13px;
          background: white;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        
        .finance-section .order-selector select:focus {
          outline: none;
          border-color: #8B5CF6;
          box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.1);
        }
        
        .finance-section .order-selector .selected-info {
          margin-top: 14px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        
        .finance-section .order-selector .selected-info .info-item {
          background: white;
          padding: 10px 14px;
          border-radius: 10px;
          border: 1px solid #E2E8F0;
          transition: all 0.3s ease;
        }
        
        .finance-section .order-selector .selected-info .info-item:hover {
          border-color: #8B5CF6;
          box-shadow: 0 4px 12px rgba(139, 92, 246, 0.08);
        }
        
        .finance-section .order-selector .selected-info .info-item .info-label {
          font-size: 9px;
          color: #94A3B8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .finance-section .order-selector .selected-info .info-item .info-value {
          font-size: 13px;
          font-weight: 600;
          color: #0F172A;
          margin-top: 2px;
        }
        
        .finance-section .input-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 16px;
        }
        
        .finance-section .input-group {
          background: white;
          border-radius: 14px;
          padding: 18px 20px;
          border: 2px solid #E2E8F0;
          transition: all 0.3s ease;
        }
        
        .finance-section .input-group:hover {
          border-color: #8B5CF6;
          box-shadow: 0 4px 20px rgba(139, 92, 246, 0.08);
          transform: translateY(-2px);
        }
        
        .finance-section .input-group .input-label {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 11px;
          font-weight: 600;
          color: #64748B;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }
        
        .finance-section .input-group .input-label .input-icon {
          font-size: 18px;
        }
        
        .finance-section .input-group input {
          width: 100%;
          padding: 8px 0;
          border: none;
          border-bottom: 2px solid #E2E8F0;
          font-size: 18px;
          font-weight: 700;
          color: #0F172A;
          background: transparent;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
        }
        
        .finance-section .input-group input:focus {
          outline: none;
          border-bottom-color: #8B5CF6;
        }
        
        .finance-section .input-group input::placeholder {
          color: #94A3B8;
          font-weight: 400;
          font-size: 14px;
        }
        
        .finance-section .input-group .input-hint {
          font-size: 10px;
          color: #94A3B8;
          margin-top: 4px;
        }
        
        .finance-section .action-row {
          display: flex;
          gap: 12px;
          margin-top: 20px;
          flex-wrap: wrap;
          align-items: center;
          padding-top: 20px;
          border-top: 2px solid #F1F5F9;
        }
        
        .finance-section .action-row .btn {
          padding: 12px 32px;
          border: none;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .finance-section .action-row .btn-primary {
          background: linear-gradient(135deg, #8B5CF6, #A78BFA);
          color: white;
          box-shadow: 0 4px 20px rgba(139, 92, 246, 0.35);
        }
        
        .finance-section .action-row .btn-primary:hover {
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 8px 30px rgba(139, 92, 246, 0.45);
        }
        
        .finance-section .action-row .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }
        
        .finance-section .action-row .btn-outline {
          background: transparent;
          border: 2px solid #E2E8F0;
          color: #475569;
        }
        
        .finance-section .action-row .btn-outline:hover {
          background: #F8FAFC;
          border-color: #8B5CF6;
          color: #8B5CF6;
          transform: translateY(-2px);
        }
        
        .finance-section .action-row .btn-danger {
          background: transparent;
          border: 2px solid #FEE2E2;
          color: #DC2626;
        }
        
        .finance-section .action-row .btn-danger:hover {
          background: #FEE2E2;
          border-color: #DC2626;
          transform: translateY(-2px);
        }
        
        .finance-section .action-row .btn-success {
          background: linear-gradient(135deg, #059669, #047857);
          color: white;
          box-shadow: 0 4px 20px rgba(5, 150, 105, 0.35);
        }
        
        .finance-section .action-row .btn-success:hover {
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 8px 30px rgba(5, 150, 105, 0.45);
        }
        
        .finance-section .preview-box {
          margin-top: 16px;
          background: linear-gradient(135deg, #F8FAFC, #F1F5F9);
          border-radius: 14px;
          padding: 20px 24px;
          border: 2px solid #8B5CF6;
          display: none;
          animation: fadeInUp 0.4s ease;
        }
        
        .finance-section .preview-box.visible {
          display: block;
        }
        
        .finance-section .preview-box .preview-title {
          font-size: 10px;
          font-weight: 600;
          color: #64748B;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 12px;
        }
        
        .finance-section .preview-box .preview-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        
        .finance-section .preview-box .preview-item {
          text-align: center;
          padding: 10px;
          background: white;
          border-radius: 10px;
          border: 1px solid #E2E8F0;
        }
        
        .finance-section .preview-box .preview-item .preview-label {
          font-size: 10px;
          color: #94A3B8;
        }
        
        .finance-section .preview-box .preview-item .preview-value {
          font-size: 18px;
          font-weight: 700;
          color: #0F172A;
          margin-top: 4px;
        }
        
        .finance-section .preview-box .preview-item .preview-value.positive {
          color: #059669;
        }
        
        .finance-section .preview-box .preview-item .preview-value.negative {
          color: #DC2626;
        }
        
        @media (max-width: 1200px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .charts-grid {
            grid-template-columns: 1fr;
          }
          .finance-section .finance-body {
            grid-template-columns: 1fr;
          }
          .finance-section .input-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
        
        @media (max-width: 768px) {
          .sidebar {
            width: 72px;
          }
          .sidebar-brand h1, .sidebar-brand p, .sidebar-menu .menu-label, 
          .sidebar-menu .menu-item span:not(.icon) {
            display: none;
          }
          .sidebar-menu .menu-item {
            justify-content: center;
            padding: 12px;
          }
          .sidebar-menu .menu-item .badge {
            display: none;
          }
          .main-content {
            margin-left: 72px;
            padding: 16px;
          }
          .stats-grid {
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }
          .finance-section .input-grid {
            grid-template-columns: 1fr;
          }
          .finance-section .preview-box .preview-grid {
            grid-template-columns: 1fr;
          }
          .page-header {
            flex-direction: column;
            align-items: flex-start;
          }
          .search-input {
            width: 100%;
          }
          .chart-container {
            height: 200px;
          }
          .finance-section .finance-header {
            flex-direction: column;
            align-items: flex-start;
          }
          .finance-section .order-selector .selected-info {
            grid-template-columns: 1fr;
          }
          .finance-section {
            padding: 16px;
          }
          .stat-card .stat-value {
            font-size: 20px;
          }
          .date-picker-dropdown {
            min-width: 280px;
            right: -40px;
          }
          .date-picker-dropdown .date-range {
            grid-template-columns: 1fr;
          }
        }
        
        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }
          .stat-card {
            padding: 12px 14px;
          }
          .stat-card .stat-value {
            font-size: 18px;
          }
          .page-header h1 {
            font-size: 22px;
          }
          .finance-section .action-row .btn {
            width: 100%;
            justify-content: center;
          }
          .date-picker-dropdown {
            min-width: 260px;
            right: -60px;
            padding: 16px;
          }
        }
      `}</style>

      <div className="dashboard-wrapper">
        {/* Sidebar */}
        <div className="sidebar">
          <div className="sidebar-brand">
            <h1>Edu<span>Store</span></h1>
            <p>Premium Admin</p>
          </div>
          
          <div className="sidebar-menu">
            <div className="menu-label">Main</div>
            
            <a href="#" className="menu-item active">
              <span className="icon">📊</span>
              <span>Dashboard</span>
            </a>
            
            <a href="#" className="menu-item">
              <span className="icon">📦</span>
              <span>Orders</span>
              <span className="badge">{orders.length}</span>
            </a>
            
            <a href="#" className="menu-item">
              <span className="icon">🛍️</span>
              <span>Products</span>
              <span className="badge">124</span>
            </a>
            
            <a href="#" className="menu-item">
              <span className="icon">👥</span>
              <span>Customers</span>
              <span className="badge">856</span>
            </a>
            
            <a href="#" className="menu-item">
              <span className="icon">⭐</span>
              <span>Reviews</span>
              <span className="badge">42</span>
            </a>
            
            <div className="menu-label">Analytics</div>
            
            <a href="#" className="menu-item">
              <span className="icon">📈</span>
              <span>Analytics</span>
            </a>
            
            <a href="#" className="menu-item">
              <span className="icon">🏷️</span>
              <span>Coupons</span>
            </a>
            
            <a href="#" className="menu-item">
              <span className="icon">💬</span>
              <span>Messages</span>
              <span className="badge">12</span>
            </a>
            
            <div className="menu-label">Settings</div>
            
            <a href="#" className="menu-item">
              <span className="icon">⚙️</span>
              <span>Settings</span>
            </a>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content">
          {/* Page Header */}
          <div className="page-header">
            <div>
              <h1>✨ Orders Dashboard</h1>
              <p>Manage and track all customer orders</p>
            </div>
            <div className="header-actions">
              <div className="search-wrapper">
                <span className="search-icon">🔍</span>
                <input
                  className="search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search orders..."
                />
              </div>
              
              {/* Date Picker */}
              <div className="date-picker-wrapper">
                <div 
                  className={`date-picker-toggle ${showDatePicker ? 'active' : ''}`}
                  onClick={() => setShowDatePicker(!showDatePicker)}
                >
                  📅 <span>{getDateRangeText()}</span>
                  {(startDate || endDate) && (
                    <span className="clear-date" onClick={(e) => { e.stopPropagation(); clearDateFilter(); }}>✕</span>
                  )}
                  <span style={{ fontSize: '10px', color: '#94A3B8' }}>▼</span>
                </div>
                
                {showDatePicker && (
                  <div className="date-picker-dropdown">
                    <div className="date-range">
                      <div className="date-group">
                        <label>From Date</label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          max={endDate || undefined}
                        />
                      </div>
                      <div className="date-group">
                        <label>To Date</label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          min={startDate || undefined}
                        />
                      </div>
                    </div>
                    <div className="date-actions">
                      <button className="btn-cancel" onClick={() => setShowDatePicker(false)}>
                        Cancel
                      </button>
                      <button 
                        className="btn-apply" 
                        onClick={() => {
                          setShowDatePicker(false);
                          setCurrentPage(1);
                        }}
                      >
                        Apply Filter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="stats-grid">
            <div className="stat-card" style={{ animationDelay: '0.1s' }}>
              <div className="stat-icon icon-gradient-1">📦</div>
              <div className="stat-label">Total Orders</div>
              <div className="stat-value">{summary.totalOrders}</div>
              <span className="stat-change up">↑ Filtered</span>
            </div>
            
            <div className="stat-card" style={{ animationDelay: '0.2s' }}>
              <div className="stat-icon icon-gradient-2">💰</div>
              <div className="stat-label">Revenue</div>
              <div className="stat-value">{formatCurrency(summary.totalRevenue)}</div>
              <span className="stat-change up">↑ Filtered</span>
            </div>
            
            <div className="stat-card" style={{ animationDelay: '0.3s' }}>
              <div className="stat-icon icon-gradient-3">📈</div>
              <div className="stat-label">Profit</div>
              <div className="stat-value" style={{ color: summary.totalProfit >= 0 ? '#059669' : '#DC2626' }}>
                {formatCurrency(summary.totalProfit)}
              </div>
              <span className="stat-change up">↑ Filtered</span>
            </div>
            
            <div className="stat-card" style={{ animationDelay: '0.4s' }}>
              <div className="stat-icon icon-gradient-4">📊</div>
              <div className="stat-label">Expenses</div>
              <div className="stat-value" style={{ color: '#DC2626' }}>{formatCurrency(summary.totalExpenses)}</div>
              <span className="stat-change down">↓ Filtered</span>
            </div>
          </div>

          {/* Charts */}
          <ChartSection />

          {/* ===== FINANCIAL MANAGEMENT - MODERN ===== */}
          <div className="finance-section">
            <div className="finance-header">
              <div className="title-group">
                <div className="icon-box">💰</div>
                <div>
                  <h3>Financial Management</h3>
                  <p>Add or edit expenses for any order</p>
                </div>
              </div>
              <div className="status-pills">
                <span className="pill manual">✏️ Manual Entry</span>
                {hasExistingFinancialData && (
                  <span className="pill added">✅ Data Added</span>
                )}
              </div>
            </div>

            <div className="finance-body">
              {/* Left Side - Order Selector */}
              <div className="order-selector">
                <span className="label">👤 Select Order</span>
                <select
                  value={selectedOrderId}
                  onChange={(e) => handleOrderSelect(e.target.value)}
                >
                  <option value="">-- Select an order --</option>
                  {orders.map((order) => (
                    <option key={order.id} value={order.id}>
                      #{order.id?.slice(0, 6)} - {order.customerName} - Rs. {order.total}
                      {order.costOfSale !== undefined && order.costOfSale !== null && order.costOfSale !== 0 ? ' ✅' : ''}
                    </option>
                  ))}
                </select>

                {selectedOrder && (
                  <div className="selected-info">
                    <div className="info-item">
                      <div className="info-label">Customer</div>
                      <div className="info-value">{selectedOrder.customerName}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Order Total</div>
                      <div className="info-value">Rs. {selectedOrder.total}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Status</div>
                      <div className="info-value">{selectedOrder.status || "Pending"}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Financial Data</div>
                      <div className="info-value" style={{ color: hasExistingFinancialData ? '#059669' : '#94A3B8' }}>
                        {hasExistingFinancialData ? '✅ Added' : '❌ Not Added'}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Side - Input Fields */}
              <div>
                <div className="input-grid">
                  <div className="input-group">
                    <div className="input-label">
                      <span>Cost of Sale</span>
                      <span className="input-icon">📦</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={costOfSale}
                      onChange={(e) => setCostOfSale(e.target.value)}
                      placeholder="Enter cost"
                    />
                    <div className="input-hint">Product cost price</div>
                  </div>

                  <div className="input-group">
                    <div className="input-label">
                      <span>Shipping Cost</span>
                      <span className="input-icon">🚚</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={shippingCost}
                      onChange={(e) => setShippingCost(e.target.value)}
                      placeholder="Enter shipping"
                    />
                    <div className="input-hint">Delivery charges</div>
                  </div>

                  <div className="input-group">
                    <div className="input-label">
                      <span>Other Expense</span>
                      <span className="input-icon">📋</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={otherExpense}
                      onChange={(e) => setOtherExpense(e.target.value)}
                      placeholder="Enter other expense"
                    />
                    <div className="input-hint">Packaging, fees, etc.</div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="action-row">
                  <button
                    className="btn btn-primary"
                    onClick={saveFinancialDetails}
                    disabled={savingFinancial || !selectedOrderId}
                  >
                    {savingFinancial ? (
                      <span>⏳ Saving...</span>
                    ) : (
                      <span>{isEditing ? '✏️ Update Details' : '💾 Save Details'}</span>
                    )}
                  </button>

                  {hasExistingFinancialData && (
                    <>
                      <button className="btn btn-outline" onClick={editFinancialDetails}>
                        ✏️ Edit
                      </button>
                      <button className="btn btn-danger" onClick={deleteFinancialDetails} disabled={savingFinancial}>
                        🗑️ Delete
                      </button>
                    </>
                  )}

                  {selectedOrderId && (
                    <button className="btn btn-outline" onClick={resetFinancialForm}>
                      🔄 Clear
                    </button>
                  )}
                </div>

                {/* Preview Box */}
                {selectedOrder && (costOfSale || shippingCost || otherExpense) && (
                  <div className={`preview-box visible`}>
                    <div className="preview-title">📊 Preview Calculation {isEditing && '(Editing)'}</div>
                    <div className="preview-grid">
                      <div className="preview-item">
                        <div className="preview-label">Order Total</div>
                        <div className="preview-value">Rs. {selectedOrder.total?.toFixed(2) || '0.00'}</div>
                      </div>
                      <div className="preview-item">
                        <div className="preview-label">Total Expense</div>
                        <div className="preview-value" style={{ color: '#DC2626' }}>
                          Rs. {(Number(costOfSale || 0) + Number(shippingCost || 0) + Number(otherExpense || 0)).toFixed(2)}
                        </div>
                      </div>
                      <div className="preview-item">
                        <div className="preview-label">Net Profit/Loss</div>
                        <div className={`preview-value ${(Number(selectedOrder.total) - (Number(costOfSale || 0) + Number(shippingCost || 0) + Number(otherExpense || 0))) < 0 ? 'negative' : 'positive'}`}>
                          Rs. {(Number(selectedOrder.total) - (Number(costOfSale || 0) + Number(shippingCost || 0) + Number(otherExpense || 0))).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Orders Table */}
          <div className="card" style={{ animation: 'fadeInUp 0.8s ease' }}>
            <div className="card-header">
              <div>
                <div className="card-title">📋 Orders</div>
                <div className="card-subtitle">
                  {filteredOrders.length} orders found
                  {(startDate || endDate) && ` • ${getDateRangeText()}`}
                </div>
              </div>
            </div>

            <div className="filter-tabs">
              {["All", "Pending", "Processing", "Completed", "Cancelled", "Returned"].map((s) => (
                <button
                  key={s}
                  className={`filter-tab ${filter === s ? 'active' : ''}`}
                  onClick={() => setFilter(s)}
                >
                  {s}
                  <span className="tab-count">{getStatusCount(s)}</span>
                </button>
              ))}
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>Loading orders...</div>
            ) : filteredOrders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>
                No orders found
                {(startDate || endDate) && <div style={{ fontSize: '13px', marginTop: '8px' }}>Try adjusting your date filter</div>}
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Customer</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th style={{ textAlign: 'right' }}>Amount</th>
                        <th style={{ textAlign: 'right' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedOrders.map((o, index) => {
                        const status = o.status || "Pending";
                        const statusColor = getStatusColor(status);
                        const hasFinancialData = o.costOfSale !== undefined && o.costOfSale !== null && o.costOfSale !== 0;

                        return (
                          <tr key={o.id} style={{ animation: `slideIn 0.4s ease ${index * 0.05}s forwards`, opacity: 0 }}>
                            <td className="order-id">#{o.id?.slice(0, 8)}</td>
                            <td>{o.customerName}</td>
                            <td style={{ color: '#64748B', fontSize: '12px' }}>{formatDate(o.createdAt)}</td>
                            <td>
                              <span className="status-badge" style={{ background: statusColor.bg, color: statusColor.text }}>
                                <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: statusColor.text, marginRight: '4px' }}></span>
                                {status}
                              </span>
                              {hasFinancialData && (
                                <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: '500', color: o.isLoss ? '#DC2626' : '#059669' }}>
                                  {o.isLoss ? '🔴 Loss' : '🟢 Profit'}
                                </span>
                              )}
                            </td>
                            <td className="amount" style={{ textAlign: 'right' }}>Rs. {o.total}</td>
                            <td style={{ textAlign: 'right' }}>
                              <button
                                onClick={() => {
                                  handleOrderSelect(o.id);
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                style={{
                                  padding: '6px 16px',
                                  border: 'none',
                                  borderRadius: '8px',
                                  background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
                                  color: 'white',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  fontFamily: 'Inter, sans-serif',
                                  transition: 'all 0.3s ease',
                                  boxShadow: '0 4px 14px rgba(139, 92, 246, 0.25)'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'scale(1.05)';
                                  e.target.style.boxShadow = '0 6px 20px rgba(139, 92, 246, 0.35)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'scale(1)';
                                  e.target.style.boxShadow = '0 4px 14px rgba(139, 92, 246, 0.25)';
                                }}
                              >
                                💰 Add Finance
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="pagination">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                      ←
                    </button>
                    {[...Array(totalPages).keys()].map((i) => (
                      <button
                        key={i}
                        className={currentPage === i + 1 ? 'active' : ''}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                      →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminOrders;